import { TabsOption, SelectOption, Nullable } from 'global';
import {
  ApplicationStageLabels,
  GetOpportunitiesTableDealerColumnsProps,
  GetOpportunitiesTablePartnerColumnsProps,
  OpportuinitiesBaseColumns,
  OpportuinitiesDealerColumns,
  OpportuinitiesPartnerColumns,
  OpportunitiesListTabs,
  OpportunitiesListTabsType,
  OpportunitiesTableColumn,
  OpportunityListTableEntity,
} from './types';
import { OpportunitiesTable } from './ui/components/opportunitiesList/OpportunitiesTable';
import CloseIcon from '@mui/icons-material/Close';
import {
  baseColumnsConfig,
  dealerColumnsConfig,
  dealPipelineTabInitialConfig,
  defaultTabValueBase,
  defaultTabValueSalesRep,
  partnerColumnsConfig,
  setOfFiltersBase,
  setOfFiltersPartner,
  setOfFiltersSalesRep,
  standardTabsInitialConfig,
} from './constants';
import { IconButton } from '@mui/material';
import { ApplicationType, FINANCING_TYPE, TableFilter, VendorOpportunityListItem } from '@trnsact/trnsact-shared-types';
import { DateFormats } from 'constants/common';
import moment from 'moment';
import { formatCurrency, OpportunityTypes } from 'utils';
import { UserProfile, VendorContactRoles } from 'types';
import { DealPipelineCard } from '../dealPipeLine';
import {
  VOFilters,
  VOFiltersFormattedForPayload,
  VOFiltersPayload,
  VOFilterType,
  VOFilterDisplayPayloadValues,
  VOFilterDisplayKeys,
  VOApiFilterType,
  VOFiltersPayloadItem,
} from '../filters';
import { Dispatch, SetStateAction } from 'react';

export const convertFiltersToTabs = (
  tableFilters: TableFilter[],
  onDelete: (tf: TableFilter) => void,
  columns: OpportunitiesTableColumn[],
  searchValue: string,
  setSearch: Dispatch<SetStateAction<string>>,
  currentFilterConfig: Nullable<Partial<VOFilters>>
): TabsOption<OpportunitiesListTabsType>[] => {
  return tableFilters.map(tf => ({
    label: tf.name,
    value: tf.tableFilterId,
    component: (
      <OpportunitiesTable
        tabValue={tf.tableFilterId}
        columns={columns}
        currentFilterConfig={currentFilterConfig}
        searchValue={searchValue}
        setSearch={setSearch}
      />
    ),
    iconEnd: (
      <IconButton
        onClick={e => {
          e.stopPropagation();
          onDelete(tf);
        }}
        color="primary"
        size="small"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    ),
  }));
};

export const getStandardTabs = (
  columns: OpportunitiesTableColumn[],
  userProfile: UserProfile,
  searchValue: string,
  setSearch: Dispatch<SetStateAction<string>>,
  currentFilterConfig: Nullable<Partial<VOFilters>>
): TabsOption<OpportunitiesListTabsType>[] => {
  return standardTabsInitialConfig.map(tab => ({
    ...tab,
    component: (
      <OpportunitiesTable
        searchValue={searchValue}
        setSearch={setSearch}
        tabValue={tab.value}
        columns={columns}
        currentFilterConfig={currentFilterConfig}
      />
    ),
    isVisible:
      userProfile.vendorContactRole !== VendorContactRoles.SalesRep
        ? true
        : tab.value === OpportunitiesListTabs.MyOpportunities,
  }));
};

export const getDealPipelineTab = (
  allowDealPipeline: boolean,
  userProfile: UserProfile,
  accountId: string
): TabsOption<OpportunitiesListTabsType> => {
  return {
    ...dealPipelineTabInitialConfig,
    component: <DealPipelineCard accountId={accountId} />,
    isVisible: allowDealPipeline && userProfile.vendorContactRole !== VendorContactRoles.SalesRep,
  };
};

export const getInitialFilterConfig = (
  tabValue: OpportunitiesListTabsType,
  userProfile: UserProfile,
  visibleColumnsFilterOptions?: SelectOption[]
): Nullable<Partial<VOFilters>> => {
  const commonFilters = {
    [VOFilterType.CREATE_DATE_RANGE]: {
      from: moment().subtract(1, 'year'),
      to: moment(),
    },
    [VOFilterType.DISPLAY]: {
      [VOFilterDisplayKeys.Active]: true,
      [VOFilterDisplayKeys.Testing]: false,
      [VOFilterDisplayKeys.Duplicate]: false,
      [VOFilterDisplayKeys.Other]: false,
    },
    [VOFilterType.SET_OF_DATA]: visibleColumnsFilterOptions,
  };

  const specificFilters =
    userProfile.vendorContactRole === VendorContactRoles.SalesRep
      ? getInitialFilterConfigForSalesRep(userProfile)
      : getInitialFilterConfigForStandardTab(tabValue, userProfile);

  return {
    ...commonFilters,
    ...specificFilters,
  };
};

const getInitialFilterConfigForStandardTab = (
  tabValue: OpportunitiesListTabsType,
  userProfile: UserProfile
): Nullable<Partial<VOFilters>> => {
  switch (tabValue) {
    case OpportunitiesListTabs.MyOpportunities:
      return {
        [VOFilterType.SALES_REP]: [{ value: userProfile.id, label: userProfile.fullName! }],
        [VOFilterType.SALES_MANAGER]: [{ value: userProfile.id, label: userProfile.fullName! }],
        [VOFilterType.FINANCE_MANAGER]: [{ value: userProfile.id, label: userProfile.fullName! }],
      };
    case OpportunitiesListTabs.Unassigned:
      return {
        [VOFilterType.SALES_REP]: [{ label: 'Unassigned', value: 'unassigned' }],
      };
    default:
      return {};
  }
};

const getInitialFilterConfigForSalesRep = (userProfile: UserProfile): Nullable<Partial<VOFilters>> => ({
  [VOFilterType.SALES_REP]: [{ value: userProfile.id, label: userProfile.fullName! }],
});

export const checkIfTabIsStandard = (tabValue: string) =>
  standardTabsInitialConfig.map(t => t.value).includes(tabValue);

export const formatOpportunityListToTableData = (
  opportunities: VendorOpportunityListItem[]
): OpportunityListTableEntity[] => {
  return opportunities.map(o => {
    const primaryContact = o.creditApplication?.creditApplicationOwner?.find(o => o!.pc);
    const signor = o.creditApplication?.creditApplicationOwner?.find(o => o!.signatureDate);

    const primaryContactFullName = [primaryContact?.firstName, primaryContact?.lastName].filter(Boolean).join(' ');
    const mainSignorFullName = primaryContact?.signatureDate
      ? primaryContactFullName
      : [signor?.firstName, signor?.lastName].filter(Boolean).join(' ');

    return {
      id: o.vendorOpportunityId!,
      createdOn: moment(o.createdOn).format(DateFormats.DateTimeShort12Hour),
      businessProcessFlowsView: o.businessProcessFlowsView as any,
      creditTiersResult: o.creditTiersResult as any,
      customerName: o.potentialCustomer?.name ?? undefined,
      contactName: primaryContactFullName,
      mainSignor: mainSignorFullName,
      applicationStage: getApplicationStageLabel(o.applicationStage),
      transactionStage: o.transactionStage ?? undefined,
      entityType: o.creditApplication?.entityType
        ? OpportunityTypes[o.creditApplication?.entityType as keyof typeof OpportunityTypes]
        : undefined,
      invoiceTotal: o.invoiceTotal ? formatCurrency(o.invoiceTotal) : undefined,
      salesRep: o.salesRepresentative?.fullName ?? undefined,
      salesManager: o.salesManager?.fullName ?? undefined,
      financeManager: o.financeManager?.fullName ?? undefined,
      location: o.location?.locationName ?? undefined,
      equipmentDescription: o.equipmentDescription,
      invoiceStatus: o.invoiceStatus ?? undefined,
      statusDetail: o.statusDetail ?? undefined,
      applicationType: getApplicationTypeLabel(o.creditApplication?.applicationType as ApplicationType),
      applyingFor: o.creditApplication?.applyingFor
        ?.map(i => FINANCING_TYPE[i as keyof typeof FINANCING_TYPE] || i)
        .join(', '),
      displayStatus: o.displayStatus ?? undefined,
      inboundDealType: o.lead?.inboundDealType ?? undefined,
      dealerName: o.dealerName ?? undefined,
      partnerRepName: o.partnerRepName ?? undefined,
      partnerLogo: o.partnerProfile?.partnerLogo ?? undefined,
      vendorProfileId: o.vendorProfileId ?? undefined,
      sourceVendorProfileId: o.sourceVendorProfileId ?? undefined,
    };
  });
};

export const getOpportunitiesTableDealerColumns = ({
  bpfTemplates,
  isTiersResultsHidden,
  allowManualVoCreation,
  isMultiLocationManagementEnabled,
}: GetOpportunitiesTableDealerColumnsProps): OpportunitiesTableColumn[] => {
  return [
    baseColumnsConfig[OpportuinitiesBaseColumns.CREATED_ON],
    ...(bpfTemplates.length ? [baseColumnsConfig[OpportuinitiesBaseColumns.BUSINESS_PROCESS_FLOWS]] : []),
    ...(!isTiersResultsHidden ? [dealerColumnsConfig[OpportuinitiesDealerColumns.TIER]] : []),
    dealerColumnsConfig[OpportuinitiesDealerColumns.SOURCE],
    baseColumnsConfig[OpportuinitiesBaseColumns.CUSTOMER],
    dealerColumnsConfig[OpportuinitiesDealerColumns.CONTACT],
    dealerColumnsConfig[OpportuinitiesDealerColumns.MAIN_SIGNOR],
    baseColumnsConfig[OpportuinitiesBaseColumns.STAGE],
    baseColumnsConfig[OpportuinitiesBaseColumns.STATUS],
    ...(allowManualVoCreation ? [dealerColumnsConfig[OpportuinitiesDealerColumns.OPPORTUNITY_TYPE]] : []),
    baseColumnsConfig[OpportuinitiesBaseColumns.AMOUNT],
    baseColumnsConfig[OpportuinitiesBaseColumns.SALES_REP],
    baseColumnsConfig[OpportuinitiesBaseColumns.SALES_MANAGER],
    baseColumnsConfig[OpportuinitiesBaseColumns.FINANCE_MANAGER],
    ...(isMultiLocationManagementEnabled ? [dealerColumnsConfig[OpportuinitiesDealerColumns.LOCATION]] : []),
    baseColumnsConfig[OpportuinitiesBaseColumns.EQUIPMENT_DETAILS],
    baseColumnsConfig[OpportuinitiesBaseColumns.INVOICE_STATUS],
    dealerColumnsConfig[OpportuinitiesDealerColumns.STATUS_DETAIL],
    dealerColumnsConfig[OpportuinitiesDealerColumns.APPLICANT_TYPE],
    dealerColumnsConfig[OpportuinitiesDealerColumns.APPLYING_FOR],
    dealerColumnsConfig[OpportuinitiesDealerColumns.DISPLAY],
    baseColumnsConfig[OpportuinitiesBaseColumns.DEAL_TYPE],
  ];
};

export const getOpportunitiesTablePartnerColumns = ({
  bpfTemplates,
}: GetOpportunitiesTablePartnerColumnsProps): OpportunitiesTableColumn[] => {
  return [
    baseColumnsConfig[OpportuinitiesBaseColumns.CREATED_ON],
    ...(bpfTemplates.length ? [baseColumnsConfig[OpportuinitiesBaseColumns.BUSINESS_PROCESS_FLOWS]] : []),
    partnerColumnsConfig[OpportuinitiesPartnerColumns.DEALER_NAME],
    baseColumnsConfig[OpportuinitiesBaseColumns.CUSTOMER],
    baseColumnsConfig[OpportuinitiesBaseColumns.SALES_REP],
    baseColumnsConfig[OpportuinitiesBaseColumns.SALES_MANAGER],
    baseColumnsConfig[OpportuinitiesBaseColumns.FINANCE_MANAGER],
    partnerColumnsConfig[OpportuinitiesPartnerColumns.PARTNER_REP],
    baseColumnsConfig[OpportuinitiesBaseColumns.STAGE],
    baseColumnsConfig[OpportuinitiesBaseColumns.STATUS],
    baseColumnsConfig[OpportuinitiesBaseColumns.AMOUNT],
    baseColumnsConfig[OpportuinitiesBaseColumns.EQUIPMENT_DETAILS],
    baseColumnsConfig[OpportuinitiesBaseColumns.INVOICE_STATUS],
    baseColumnsConfig[OpportuinitiesBaseColumns.DEAL_TYPE],
  ];
};

export const getSetOfDataFilterOptions = (columns: OpportunitiesTableColumn[]): SelectOption[] =>
  columns.map(c => ({
    label: c.header,
    value: c.renderKey,
  }));

export const formatTableFilterFromResponse = (tableFilter: TableFilter) => {
  return {
    ...tableFilter,
    config: {
      ...tableFilter.config,
      ...(tableFilter.config[VOFilterType.CREATE_DATE_RANGE] && {
        [VOFilterType.CREATE_DATE_RANGE]: {
          from: moment(tableFilter.config[VOFilterType.CREATE_DATE_RANGE].from),
          to: moment(tableFilter.config[VOFilterType.CREATE_DATE_RANGE].to),
        },
      }),
    },
  };
};

const getApplicationStageLabel = (applicationStage?: Nullable<string>) => {
  if (!applicationStage) return '';
  const isInEnum = (Object.values(ApplicationStageLabels) as string[]).includes(applicationStage);
  return isInEnum
    ? ApplicationStageLabels[applicationStage as keyof typeof ApplicationStageLabels]
    : applicationStage.charAt(0).toUpperCase() + applicationStage.slice(1);
};

const getApplicationTypeLabel = (applicationType?: Nullable<ApplicationType>) => {
  if (!applicationType) return '';
  if (
    [
      ApplicationType.Commercial,
      ApplicationType.CommercialWithGuarantor,
      ApplicationType.CommercialWithMultipleGuarantors,
    ].includes(applicationType)
  ) {
    return 'Commercial';
  }
  if (applicationType === ApplicationType.Individual) {
    return 'Individual';
  }
  if (applicationType === ApplicationType.IndividualWithSecondGuarantor) {
    return 'Joint';
  }
};

const payloadFilterFormatters: Partial<Record<VOFilterType, (value: any) => any>> = {
  [VOFilterType.CREATE_DATE_RANGE]: (value: VOFilters[VOFilterType.CREATE_DATE_RANGE]) => ({
    from: value.from.toISOString(),
    to: value.to.toISOString(),
  }),
  [VOFilterType.DISPLAY]: (value: VOFilters[VOFilterType.DISPLAY]) =>
    (Object.keys(value) as VOFilterDisplayKeys[])
      .filter(key => !!value[key])
      .map(key => VOFilterDisplayPayloadValues[key]),
  [VOFilterType.STATUS]: (value: VOFilters[VOFilterType.STATUS]) => value.map(v => v.value),
  [VOFilterType.LOCATION]: (value: VOFilters[VOFilterType.LOCATION]) => value.map(v => v.value),
  [VOFilterType.SALES_REP]: (value: VOFilters[VOFilterType.SALES_REP]) =>
    value.map(v => (v.value === 'unassigned' ? null : v.value)),
  [VOFilterType.OPPORTUNITY_TYPE]: (value: VOFilters[VOFilterType.OPPORTUNITY_TYPE]) => value.map(v => v.value),
  [VOFilterType.APPLYING_FOR]: (value: VOFilters[VOFilterType.APPLYING_FOR]) => value.map(v => v.value),
  [VOFilterType.SALES_MANAGER]: (value: VOFilters[VOFilterType.SALES_MANAGER]) =>
    value.map(v => (v.value === 'unassigned' ? null : v.value)),
  [VOFilterType.FINANCE_MANAGER]: (value: VOFilters[VOFilterType.FINANCE_MANAGER]) =>
    value.map(v => (v.value === 'unassigned' ? null : v.value)),
  [VOFilterType.TIERS]: (value: VOFilters[VOFilterType.TIERS]) => Object.keys(value).filter(key => !!value[key]),
  [VOFilterType.STAGE]: (value: VOFilters[VOFilterType.STAGE]) => Object.keys(value).filter(key => !!value[key]),
  [VOFilterType.PAYMENT_TYPE]: (value: VOFilters[VOFilterType.PAYMENT_TYPE]) =>
    Object.keys(value).filter(key => !!value[key]),
  [VOFilterType.BPF]: (value: VOFilters[VOFilterType.BPF]) => ({
    stages: value.stages.map(v => v.value),
    departments: value.departments.map(v => v.value),
  }),
};

const getPayloadFilter = (key: VOFilterType, v: any): Nullable<VOFiltersPayloadItem[]> => {
  switch (key) {
    case VOFilterType.SET_OF_DATA:
      return null;
    case VOFilterType.CREATE_DATE_RANGE: {
      const value = v as VOFilters[VOFilterType.CREATE_DATE_RANGE];
      return [
        {
          key: VOApiFilterType.CREATE_DATE_RANGE,
          value: {
            from: value.from.toISOString(),
            to: value.to.toISOString(),
          },
        },
      ];
    }
    case VOFilterType.DISPLAY: {
      const typedValue = v as VOFilters[VOFilterType.DISPLAY];
      const value = (Object.keys(typedValue) as VOFilterDisplayKeys[])
        .filter(key => !!typedValue[key])
        .map(key => VOFilterDisplayPayloadValues[key]);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.DISPLAY,
          value,
        },
      ];
    }
    case VOFilterType.STATUS: {
      const typedValue = v as VOFilters[VOFilterType.STATUS];
      const value = typedValue.map(v => v.value);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.STATUS,
          value,
        },
      ];
    }
    case VOFilterType.LOCATION: {
      const typedValue = v as VOFilters[VOFilterType.LOCATION];
      const value = typedValue.map(v => v.value);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.LOCATION,
          value,
        },
      ];
    }
    case VOFilterType.OPPORTUNITY_TYPE: {
      const typedValue = v as VOFilters[VOFilterType.OPPORTUNITY_TYPE];
      const value = typedValue.map(v => v.value);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.OPPORTUNITY_TYPE,
          value,
        },
      ];
    }
    case VOFilterType.APPLYING_FOR: {
      const typedValue = v as VOFilters[VOFilterType.APPLYING_FOR];
      const value = typedValue.map(v => v.value);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.APPLYING_FOR,
          value,
        },
      ];
    }
    case VOFilterType.SALES_REP: {
      const typedValue = v as VOFilters[VOFilterType.SALES_REP];
      const value = typedValue.map(v => (v.value === 'unassigned' ? null : v.value));
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.SALES_REP,
          value,
        },
      ];
    }
    case VOFilterType.SALES_MANAGER: {
      const typedValue = v as VOFilters[VOFilterType.SALES_MANAGER];
      const value = typedValue.map(v => (v.value === 'unassigned' ? null : v.value));
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.SALES_MANAGER,
          value,
        },
      ];
    }
    case VOFilterType.FINANCE_MANAGER: {
      const typedValue = v as VOFilters[VOFilterType.FINANCE_MANAGER];
      const value = typedValue.map(v => (v.value === 'unassigned' ? null : v.value));
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.FINANCE_MANAGER,
          value,
        },
      ];
    }
    case VOFilterType.TIERS: {
      const typedValue = v as VOFilters[VOFilterType.TIERS];
      const value = Object.keys(typedValue).filter(key => !!typedValue[key]);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.TIERS,
          value,
        },
      ];
    }
    case VOFilterType.STAGE: {
      const typedValue = v as VOFilters[VOFilterType.STAGE];
      const value = Object.keys(typedValue).filter(key => !!typedValue[key]);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.STAGE,
          value,
        },
      ];
    }
    case VOFilterType.PAYMENT_TYPE: {
      const typedValue = v as VOFilters[VOFilterType.PAYMENT_TYPE];
      const value = Object.keys(typedValue).filter(key => !!typedValue[key]);
      if (!value.length) return null;
      return [
        {
          key: VOApiFilterType.PAYMENT_TYPE,
          value,
        },
      ];
    }
    case VOFilterType.BPF: {
      const typedValue = v as VOFilters[VOFilterType.BPF];
      const result: VOFiltersPayloadItem[] = [];
      if (typedValue.stages.length) {
        result.push({ key: VOApiFilterType.BPF_STAGE, value: typedValue.stages.map(v => v.value) });
      }
      if (typedValue.departments.length) {
        result.push({ key: VOApiFilterType.BPF_DEPARTMENT, value: typedValue.departments.map(v => v.value) });
      }
      return result;
    }
    default:
      return null;
  }
};

export const formatFilterForPayload = (currentFilterConfig: Nullable<Partial<VOFilters>>): VOFiltersPayload => {
  if (!currentFilterConfig) return [];

  let result: VOFiltersPayload = [];
  const filterConfig = Object.entries(currentFilterConfig) as [
    keyof typeof currentFilterConfig,
    (typeof currentFilterConfig)[keyof typeof currentFilterConfig],
  ][];
  for (const [key, value] of filterConfig) {
    const payloadFilter = getPayloadFilter(key, value);

    if (payloadFilter === null) {
      continue;
    }

    result = [...result, ...payloadFilter];
  }

  return result;
};

export const getDefaultTabValue = (userProfile: UserProfile) => {
  return userProfile.vendorContactRole === VendorContactRoles.SalesRep ? defaultTabValueSalesRep : defaultTabValueBase;
};

export const getAvailableSetOfFilters = (userProfile: UserProfile, isPartner: boolean) => {
  if (isPartner) {
    return setOfFiltersPartner;
  }
  return userProfile.vendorContactRole === VendorContactRoles.SalesRep ? setOfFiltersSalesRep : setOfFiltersBase;
};
